import { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause } from "react-icons/fa";

export default function AudioMessage({ fileUrl }) {
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  // Stop this audio if another one starts
  useEffect(() => {
    const handleStopOthers = (e) => {
      if (e.detail !== audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    };
    window.addEventListener("audio-play", handleStopOthers);
    return () => window.removeEventListener("audio-play", handleStopOthers);
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Tell others to stop
      window.dispatchEvent(new CustomEvent("audio-play", { detail: audioRef.current }));
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    const current = audioRef.current.currentTime;
    const total = audioRef.current.duration;
    setProgress((current / total) * 100);
  };

  const handleLoaded = () => {
    setDuration(audioRef.current.duration);
  };

  // ✅ Seek on click
  const handleSeek = (e) => {
    if (!audioRef.current || !progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left; // position of click inside bar
    const width = rect.width;
    const newTime = (clickX / width) * audioRef.current.duration;
    audioRef.current.currentTime = newTime;
    setProgress((newTime / audioRef.current.duration) * 100);
  };

  const formatTime = (secs) => {
    if (isNaN(secs)) return "0:00";
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  return (
    <div className="flex items-center gap-3 bg-green-100 rounded-2xl px-3 py-2 max-w-xs">
      {/* Play/Pause Button */}
      <button
        onClick={togglePlay}
        className="p-2 bg-green-500 text-white rounded-full"
      >
        {isPlaying ? <FaPause size={16} /> : <FaPlay size={16} />}
      </button>

      {/* Progress Bar + Time */}
      <div className="flex-1">
        <div
          ref={progressBarRef}
          className="h-1 bg-gray-300 rounded-full cursor-pointer"
          onClick={handleSeek}
        >
          <div
            className="h-1 bg-green-600 rounded-full"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="text-xs text-gray-600 mt-1">
          {formatTime(duration * (progress / 100))} / {formatTime(duration)}
        </p>
      </div>

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={fileUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoaded}
        onEnded={() => setIsPlaying(false)}
      />
    </div>
  );
}
